import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = memo(() => {
  const { t, language, changeLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top when clicking nav links
  const handleNavClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/technology', label: t('nav.technology') },
    { path: '/machinery', label: t('nav.machinery') },
    { path: '/career', label: t('nav.career') },
    { path: '/galerie', label: 'Galerie' },
    { path: '/contact', label: t('nav.contact') }
  ];

  const languages = [
    { code: 'cs', label: 'CZ' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' }
  ];

  // Text shadow style for readability
  const textShadowStyle = !isScrolled ? { textShadow: '0px 2px 4px rgba(0,0,0,0.6)' } : {};

  return (
    <header 
      data-testid="main-header"
      className={`top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'fixed bg-industrial-black/95 backdrop-blur-sm border-b border-zinc-800' 
          : 'absolute bg-transparent'
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center group"
            onClick={handleNavClick}
          >
            <span className="font-heading font-bold text-2xl text-industrial-yellow tracking-wide" style={textShadowStyle}>KAISRLÍK</span>
            <span className={`text-sm ml-2 hidden sm:inline transition-colors ${isScrolled ? 'text-gray-400' : 'text-white'}`} style={textShadowStyle}>spol. s r.o.</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? 'text-industrial-yellow' 
                    : isScrolled ? 'text-gray-400 hover:text-white' : 'text-white hover:text-industrial-yellow'
                }`}
                style={textShadowStyle}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Certifikáty - special yellow link */}
            <Link
              to="/certifikaty"
              onClick={handleNavClick}
              data-testid="nav-link-certifikaty"
              className={`font-mono text-xs uppercase tracking-widest transition-colors duration-300 ml-4 ${
                location.pathname === '/certifikaty' 
                  ? 'text-industrial-yellow' 
                  : 'text-industrial-yellow hover:text-yellow-300'
              }`}
              style={textShadowStyle}
            >
              {language === 'cs' ? 'Certifikáty' : language === 'en' ? 'Certificates' : 'Zertifikate'}
            </Link>
          </nav>

          {/* Language Switcher & Mobile Menu Button */}
          <div className="flex items-center gap-6">
            {/* Language Switcher */}
            <div className="hidden md:flex items-center gap-2" data-testid="language-switcher">
              {languages.map((lang, index) => (
                <React.Fragment key={lang.code}>
                  <button
                    onClick={() => changeLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                    className={`font-mono text-xs tracking-widest transition-colors duration-300 ${
                      language === lang.code 
                        ? 'text-industrial-yellow' 
                        : isScrolled ? 'text-gray-500 hover:text-white' : 'text-white/80 hover:text-white'
                    }`}
                    style={textShadowStyle}
                  >
                    {lang.label}
                  </button>
                  {index < languages.length - 1 && (
                    <span className={isScrolled ? 'text-gray-700' : 'text-white/50'} style={textShadowStyle}>/</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="xl:hidden text-white hover:text-industrial-yellow transition-colors"
              style={textShadowStyle}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          data-testid="mobile-menu"
          className={`xl:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-screen pb-6' : 'max-h-0'
          } ${!isScrolled && isMobileMenuOpen ? 'bg-black/80 backdrop-blur-sm' : ''}`}
        >
          <nav className="flex flex-col gap-4 pt-4 border-t border-zinc-800">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`font-mono text-sm uppercase tracking-widest py-2 transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? 'text-industrial-yellow' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Certifikáty - special yellow link */}
            <Link
              to="/certifikaty"
              onClick={handleNavClick}
              data-testid="mobile-nav-link-certifikaty"
              className={`font-mono text-sm uppercase tracking-widest py-2 transition-colors duration-300 ${
                location.pathname === '/certifikaty' 
                  ? 'text-industrial-yellow' 
                  : 'text-industrial-yellow hover:text-yellow-300'
              }`}
            >
              {language === 'cs' ? 'Certifikáty' : language === 'en' ? 'Certificates' : 'Zertifikate'}
            </Link>
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-4 pt-4 border-t border-zinc-800 md:hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setIsMobileMenuOpen(false);
                  }}
                  data-testid={`mobile-lang-${lang.code}`}
                  className={`font-mono text-sm tracking-widest transition-colors duration-300 ${
                    language === lang.code 
                      ? 'text-industrial-yellow' 
                      : 'text-gray-500 hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
});

export default Header;
